import styles from './index.module.css'

import { MdCheck } from '@react-icons/all-files/md/MdCheck'
import React from 'react'

import { formatTime } from '@disco/utils/media-player'

import { Box, Button } from 'components/ds'

import { useSegmentDrag } from './use-segment-drag'

const HandleBars = ({ count = 2 }: { count?: number }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <span key={i} className={styles.segmentHandleBar} />
    ))}
  </>
)

type Props = {
  segmentStart: number
  segmentEnd: number
  duration: number
  maxSegmentLength: number
  onSegmentChange?: (start: number, end: number) => void
  onSegmentCommit?: (start: number, end: number) => void
  showApplyButton?: boolean
  isApplying?: boolean
  onApply?: () => void
}

/**
 * SegmentSelector displays a visual overlay on the waveform showing the
 * selected segment for similarity search. The segment is highlighted in
 * yellow with handles at each edge, with timestamp labels above each handle.
 */
const SegmentSelector = ({
  segmentStart,
  segmentEnd,
  duration,
  maxSegmentLength,
  onSegmentChange,
  onSegmentCommit,
  showApplyButton,
  isApplying,
  onApply,
}: Props) => {
  const {
    containerRef,
    segmentRef,
    startLabelRef,
    endLabelRef,
    handleLeftHandleDrag,
    handleRightHandleDrag,
    isDragging,
    handleBodyMouseDown,
    handleBodyTouchStart,
    handleKeyDown,
    handleKeyUp,
  } = useSegmentDrag({
    segmentStart,
    segmentEnd,
    duration,
    maxSegmentLength,
    onSegmentChange,
    onSegmentCommit,
  })

  const segmentClassName = [
    styles.segment,
    isDragging && styles.segmentDragging,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box
      ref={containerRef}
      className={styles.container}
      data-testid="segment-selector"
    >
      <Box asChild className={segmentClassName}>
        <div
          ref={segmentRef}
          onMouseDown={onSegmentChange ? handleBodyMouseDown : undefined}
          onTouchStart={onSegmentChange ? handleBodyTouchStart : undefined}
          onKeyDown={onSegmentChange ? handleKeyDown : undefined}
          onKeyUp={onSegmentChange ? handleKeyUp : undefined}
          tabIndex={onSegmentChange ? 0 : -1}
          data-testid="segment-slider"
          role={onSegmentChange ? 'slider' : undefined}
          aria-label={
            onSegmentChange
              ? `Segment: ${formatTime(segmentStart)} – ${formatTime(
                  segmentEnd,
                )} (drag or use arrow keys)`
              : undefined
          }
          aria-valuemin={onSegmentChange ? 0 : undefined}
          aria-valuemax={onSegmentChange ? duration : undefined}
          aria-valuenow={onSegmentChange ? Math.round(segmentStart) : undefined}
          aria-valuetext={
            onSegmentChange
              ? `${formatTime(segmentStart)} to ${formatTime(segmentEnd)}`
              : undefined
          }
        >
          {/* Updated imperatively during drag via applySegmentToDOM */}
          <Box
            className={`${styles.segmentHandle} ${styles.segmentHandleLeft}`}
            onMouseDown={onSegmentChange ? handleLeftHandleDrag : undefined}
            onTouchStart={onSegmentChange ? handleLeftHandleDrag : undefined}
          >
            <Box className={styles.segmentHandleBars}>
              <Box
                ref={startLabelRef}
                className={styles.timestampLabel}
                data-testid="segment-start-label"
              />
              <HandleBars />
            </Box>
          </Box>
          {/* CSS visibility toggled to avoid mount/unmount during drag */}
          <Box
            className={`${styles.dragHandle} ${
              showApplyButton || isDragging ? styles.dragHandleHidden : ''
            }`}
            data-testid="segment-drag-handle"
          >
            <HandleBars count={3} />
          </Box>
          <Button
            size="1"
            className={`${styles.applyButton} ${
              // Hide during drag to avoid mis-clicks
              !showApplyButton || isDragging ? styles.applyButtonHidden : ''
            }`}
            onClick={e => {
              e.stopPropagation()
              onApply?.()
            }}
            onMouseDown={e => e.stopPropagation()}
            disabled={isDragging || isApplying}
            data-testid="segment-apply-button"
          >
            <MdCheck className={styles.applyButtonIcon} />
            OK
          </Button>
          <Box
            className={`${styles.segmentHandle} ${styles.segmentHandleRight}`}
            onMouseDown={onSegmentChange ? handleRightHandleDrag : undefined}
            onTouchStart={onSegmentChange ? handleRightHandleDrag : undefined}
          >
            <Box className={styles.segmentHandleBars}>
              <HandleBars />
              <Box
                ref={endLabelRef}
                className={styles.timestampLabel}
                data-testid="segment-end-label"
              />
            </Box>
          </Box>
        </div>
      </Box>
    </Box>
  )
}

export default SegmentSelector
